import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Camera, ImagePlus, X, Send, Footprints } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Lawn Care", "Fertilizer", "Flowers & Beds", "Trees & Shrubs", "Irrigation", "Pest & Weed", "Cleanup", "Hardscape", "Other"];

export default function EstateWalkthrough({ properties, employees }) {
  const [propertyId, setPropertyId] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Lawn Care");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState("normal");
  const [assignee, setAssignee] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [captured, setCaptured] = useState([]);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!propertyId && properties.length > 0) setPropertyId(properties[0].id);
  }, [properties]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = (list) => {
    const picked = [...list].map((file) => ({ file, preview: URL.createObjectURL(file), key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}` }));
    setFiles((s) => [...s, ...picked]);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!propertyId || !title.trim()) return;
    setSaving(true);
    try {
      const { data: task } = await api.post("/estate/tasks", {
        property_id: propertyId, title, category, notes, priority,
        assigned_to_id: assignee || null, scheduled_date: schedDate || null,
      });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        try { await api.post(`/estate/tasks/${task.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (err) { toast.error("A photo failed to upload"); }
      }
      setCaptured((s) => [{ ...task, photo_count: files.length }, ...s]);
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setTitle(""); setNotes(""); setPriority("normal"); setFiles([]);
      toast.success("Task captured");
    } catch (err) { toast.error("Could not save task"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="estate-walkthrough">
      <form onSubmit={save} className="bg-white border border-stone-200 p-5 space-y-3">
        <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-2"><Footprints size={14} /> Capture a task</div>
        <select data-testid="walk-property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} required className="w-full h-11 border border-stone-300 px-2 text-sm font-semibold">
          <option value="">— Select property —</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input data-testid="walk-title" required value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs to be done? *" className="w-full h-11 border border-stone-300 px-3 text-sm" />
        <div className="flex flex-wrap gap-1.5" data-testid="walk-categories">
          {CATS.map((c) => (
            <button type="button" key={c} data-testid={`walk-cat-${c.replace(/\s+|&/g, "-")}`} onClick={() => setCategory(c)}
              className={`h-9 px-3 text-xs font-semibold border ${category === c ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}>{c}</button>
          ))}
        </div>
        <textarea data-testid="walk-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder="Notes — location on property, materials needed…" className="w-full border border-stone-300 p-3 text-sm" />

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="walk-camera-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="walk-photo-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="walk-take-photo" onClick={() => cameraRef.current?.click()}
            className="h-10 px-4 border border-stone-300 font-bold uppercase text-xs flex items-center gap-2"><Camera size={14} /> Take Photo</button>
          <button type="button" data-testid="walk-add-photos" onClick={() => galleryRef.current?.click()}
            className="h-10 px-4 border border-stone-300 font-bold uppercase text-xs flex items-center gap-2"><ImagePlus size={14} /> Add Photos</button>
        </div>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="walk-previews">
            {files.map((f) => (
              <div key={f.key} className="relative">
                <img src={f.preview} alt="preview" className="h-20 w-20 object-cover border border-stone-200" />
                <button type="button" onClick={() => { URL.revokeObjectURL(f.preview); setFiles((s) => s.filter((x) => x.key !== f.key)); }}
                  className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white flex items-center justify-center"><X size={12} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select data-testid="walk-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="h-11 border border-stone-300 px-2 text-sm">
            <option value="urgent">Urgent</option><option value="high">High</option>
            <option value="normal">Normal</option><option value="low">Low</option>
          </select>
          <select data-testid="walk-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="h-11 border border-stone-300 px-2 text-sm">
            <option value="">Assign later…</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
          <input data-testid="walk-date" type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="h-11 border border-stone-300 px-2 text-sm" />
        </div>
        <button data-testid="walk-save" disabled={saving}
          className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center justify-center gap-2 disabled:opacity-50">
          <Send size={14} /> {saving ? "Saving…" : "Save Task & Next"}
        </button>
      </form>

      <div className="space-y-2">
        <div className="text-xs font-bold uppercase text-stone-500">Captured this walkthrough ({captured.length})</div>
        {captured.map((t) => (
          <div key={t.id} className="bg-white border border-stone-200 p-3 text-sm" data-testid={`captured-${t.id}`}>
            <span className="font-bold">{t.title}</span>
            <span className="text-stone-400"> · {t.category} · {t.property_name}{t.photo_count ? ` · ${t.photo_count} photo${t.photo_count > 1 ? "s" : ""}` : ""}{t.assigned_to ? ` · → ${t.assigned_to}` : ""}</span>
          </div>
        ))}
        {captured.length === 0 && (
          <div className="bg-white border border-dashed border-stone-300 p-8 text-center text-stone-400 text-sm">
            Walk the property and capture tasks — they'll stack up here as you go
          </div>
        )}
      </div>
    </div>
  );
}
